import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { isHTTPError } from 'ky'
import { FileText, ExternalLink, RefreshCw, AlertCircle, Plus } from 'lucide-react'
import toast from 'react-hot-toast'
import type {
  AuditorStatus,
  BridgeLetter,
  ReviewDocumentsResponse,
  ReviewDocument,
  RetryDrataResponse,
} from '@common/types'
import { resolveComplianceStatus } from '@common/utils/validity'
import { DRATA_UPLOAD_ERROR_LABELS, type DrataUploadError } from '@common/utils/drataErrors'
import { api } from '@frontend/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@frontend/components/ui/card'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { DocumentStatusBadge } from '@frontend/components/ui/status-badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@frontend/components/ui/table'
import { GenerateDocumentsDialog } from './GenerateDocumentsDialog'

interface DocumentsSectionProps {
  reviewId: number
  vendorName: string
  reviewStatus: string
  auditPeriodEnd: string | null
  archivedAt: string | null
  auditorStatus: AuditorStatus | null
  bridgeLetter: BridgeLetter | null
}

async function fetchDocuments(reviewId: number): Promise<ReviewDocumentsResponse> {
  return api.get(`reviews/${reviewId}/documents`).json<ReviewDocumentsResponse>()
}

async function retryDrataUpload(documentId: string): Promise<RetryDrataResponse> {
  return api.post(`documents/${documentId}/retry-drata`).json<RetryDrataResponse>()
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function DocumentsSection({
  reviewId,
  vendorName,
  reviewStatus,
  auditPeriodEnd,
  archivedAt,
  auditorStatus,
  bridgeLetter,
}: DocumentsSectionProps) {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [retryingDocId, setRetryingDocId] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['review', String(reviewId), 'documents'],
    queryFn: () => fetchDocuments(reviewId),
  })

  const retryMutation = useMutation({
    mutationFn: retryDrataUpload,
    onMutate: (documentId) => {
      setRetryingDocId(documentId)
    },
    onSuccess: (result) => {
      if (result.warning) {
        toast(result.warning, { icon: '⚠️' })
      }
      toast.success('Uploaded to Drata')
    },
    onError: (err) => {
      if (!isHTTPError(err)) {
        toast.error('Failed to retry Drata upload')
        return
      }
      // A failed push carries a DrataUploadError code; the route's pre-flight
      // guards carry a prose message. Both arrive here as `error`. ky only parses
      // `data` for a JSON body — an empty one (a proxy 502) leaves it undefined,
      // and an HTTPException reply (the 403 that says to reconnect Google) is bare
      // text — so reading it unguarded would throw and swallow the failure again.
      const data = (typeof err.data === 'object' && err.data !== null ? err.data : {}) as {
        error?: string
        warning?: string
      }
      if (data.warning) {
        toast(data.warning, { icon: '⚠️' })
      }
      const error = data.error ?? (typeof err.data === 'string' ? err.data : undefined)
      const reason =
        error && error in DRATA_UPLOAD_ERROR_LABELS ? DRATA_UPLOAD_ERROR_LABELS[error as DrataUploadError] : error
      toast.error(reason || 'Failed to retry Drata upload')
    },
    onSettled: () => {
      setRetryingDocId(null)
      queryClient.invalidateQueries({ queryKey: ['review', String(reviewId), 'documents'] })
    },
  })

  const isApproved = reviewStatus === 'complete'
  // A currently-valid bridge letter keeps an expired report uploadable (days 90-180).
  const isValid = resolveComplianceStatus({ auditPeriodEnd, bridgeLetter }).isUploadable
  const isArchived = !!archivedAt
  const isAuditorArchived = auditorStatus === 'archived'
  const canGenerate = isApproved && isValid && !isArchived && !isAuditorArchived
  // Mirrors the backend's choke-point gate: a push the server would reject as
  // `not_uploadable` must not offer a retry button that can never succeed.
  const canRetryUploads = isValid && !isArchived
  const documents = data?.documents || []

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Generated Documents
            </CardTitle>
            {canGenerate && (
              <Button size="sm" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Generate Documents
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!isApproved && (
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md text-sm text-muted-foreground mb-4">
              <AlertCircle className="h-4 w-4" />
              Review must be approved before generating documents
            </div>
          )}
          {isApproved && !isValid && !isArchived && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm mb-4">
              <AlertCircle className="h-4 w-4" />
              Cannot generate documents for an expired report. The report has been invalid for more than 3 months.
              Upload or extend a bridge letter to keep it usable.
            </div>
          )}
          {isArchived && (
            <div className="flex items-center gap-2 p-3 bg-slate-100 text-slate-700 rounded-md text-sm mb-4">
              <AlertCircle className="h-4 w-4" />
              This review is archived. Documents cannot be generated or uploaded.
            </div>
          )}
          {isApproved && !isArchived && isAuditorArchived && (
            <div className="flex items-center gap-2 p-3 bg-slate-100 text-slate-700 rounded-md text-sm mb-4">
              <AlertCircle className="h-4 w-4" />
              The linked auditor has been archived. Regenerate the review to produce new documents.
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center py-8">
              <Spinner size="sm" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4" />
              Failed to load documents
            </div>
          )}

          {!isLoading && !error && documents.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No documents generated yet</p>
              {canGenerate && (
                <p className="text-xs mt-1">Click "Generate Documents" to create compliance documents for clients</p>
              )}
            </div>
          )}

          {!isLoading && !error && documents.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    document={doc}
                    onRetryDrata={() => retryMutation.mutate(doc.id)}
                    isRetrying={retryingDocId === doc.id}
                    canRetryDrata={canRetryUploads && doc.status === 'generated'}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <GenerateDocumentsDialog
        reviewId={reviewId}
        vendorName={vendorName}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  )
}

interface DocumentRowProps {
  document: ReviewDocument
  onRetryDrata: () => void
  isRetrying: boolean
  canRetryDrata: boolean
}

function DocumentRow({ document, onRetryDrata, isRetrying, canRetryDrata }: DocumentRowProps) {
  return (
    <TableRow>
      <TableCell>
        <span className="font-medium">{document.clientName}</span>
      </TableCell>
      <TableCell>
        <DocumentStatusBadge status={document.status} />
      </TableCell>
      <TableCell>
        <div className="text-sm">{formatDateTime(document.createdAt)}</div>
        {document.generatedBy && <div className="text-xs text-muted-foreground">by {document.generatedBy}</div>}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {document.googleDriveId && (
            <a
              href={`https://docs.google.com/document/d/${document.googleDriveId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
            >
              <ExternalLink className="h-3 w-3" />
              View
            </a>
          )}
          {canRetryDrata && (
            <Button variant="ghost" size="sm" onClick={onRetryDrata} disabled={isRetrying} title="Retry Drata upload">
              {isRetrying ? <Spinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}
